/**
 * Subgroup hub cards — clinicians + MVP assignment + manage members.
 * Create/edit roster form stays in SubgroupBuilder; this is the at-a-glance home.
 */
import { useMemo, useState, type MutableRefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  useNpiProfiles, useSubgroups, type NpiProfileRow, type Subgroup,
} from '@/hooks/queries/useMvpQueries';
import { MipsDataService, type MvpDefinition } from '@/lib/mips-data-service';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { Check, ChevronDown, ChevronRight, Download, Pencil, Star, Trash2, X } from 'lucide-react';

function csvEscape(v: unknown) {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, headers: string[], rows: unknown[][]) {
  const lines = [headers.join(','), ...rows.map(r => r.map(csvEscape).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Rich clinician export columns (section-wide + per-card). */
const CLINICIAN_EXPORT_HEADERS = [
  'program_year',
  'subgroup_id',
  'subgroup_name',
  'specialty_hint',
  'clinician_count',
  'mvp_id',
  'mvp_title',
  'mvp_clinical_topic',
  'mvp_status',
  'registered_with_cms',
  'npi',
  'clinician_name',
  'clinician_type',
  'specialty_code',
  'specialty_description',
  'roster_origin',
  'mips_eligible_individual',
  'mips_eligible_group',
  'allowed_charges',
  'medicare_patients',
  'cms_allowed_amt',
  'cms_beneficiaries',
  'cms_dual_count',
  'cms_avg_risk_score',
  'practice_match',
  'confirmed_org_name',
  'suggested_org_name',
  'org_count',
  'org_names',
  'orgs_group_eligible',
  'orgs_individual_eligible',
  'match_tier',
  'hydrated_at',
] as const;

function yesNo(v: boolean | null | undefined) {
  if (v == null) return '';
  return v ? 'Yes' : 'No';
}

function practiceMatchLabel(p: NpiProfileRow | undefined) {
  if (!p) return 'no_profile';
  if (p.force_include) return 'force_included';
  if (p.counts_for_practice) return 'confirmed';
  if (p.suggested_org_name) return 'suggest_confirm';
  return 'not_on_practice';
}

function buildClinicianExportRow(args: {
  programYear: number;
  sg: Subgroup;
  mvpId: string;
  mvpTitle: string;
  mvpTopic: string;
  mvpStatus: string;
  registeredCms: string;
  npi: string;
  profile?: NpiProfileRow;
}): unknown[] {
  const p = args.profile;
  const orgs = p?.orgs ?? [];
  return [
    args.programYear,
    args.sg.id,
    args.sg.name,
    args.sg.specialty_hint ?? '',
    args.sg.members.length,
    args.mvpId,
    args.mvpTitle,
    args.mvpTopic,
    args.mvpStatus,
    args.registeredCms,
    args.npi,
    p?.clinician_name ?? '',
    p?.type_description || p?.clinician_type || '',
    p?.specialty_code ?? '',
    cleanSpecialty(p?.specialty_desc) || '',
    p?.origin === 'qpp_file' ? 'qpp_eligibility_file' : (p?.origin ?? ''),
    yesNo(p?.mips_eligible_individual),
    yesNo(p?.mips_eligible_group),
    p?.allowed_charges ?? '',
    p?.medicare_patients ?? '',
    p?.mdcr_allowed_amt ?? '',
    p?.mdcr_benes ?? '',
    p?.dual_cnt ?? '',
    p?.avg_risk_score ?? '',
    practiceMatchLabel(p),
    p?.confirmed_org_name ?? '',
    p?.suggested_org_name ?? '',
    p?.org_count ?? '',
    (p?.org_names ?? orgs.map(o => o.name)).filter(Boolean).join(' | '),
    orgs.filter(o => o.groupEligible === true).map(o => o.name).join(' | '),
    orgs.filter(o => o.individualEligible === true).map(o => o.name).join(' | '),
    p?.match_tier ?? '',
    p?.fetched_at ?? '',
  ];
}

function slugForFilename(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48) || 'subgroup';
}

export interface SubgroupSelectionRow {
  id: number;
  mvp_id: string;
  entity_type: string;
  subgroup_id: number | null;
  subgroup_name?: string | null;
  registered?: boolean;
}

interface SubgroupMvpAssignSectionProps {
  practiceId: string | number;
  programYear: number;
  selections: SubgroupSelectionRow[];
  onManageMembers: (sg: Subgroup) => void;
  /** Nest under SubgroupBuilder — no second section title; Export via exportAllRef */
  embedded?: boolean;
  /** Parent wires Export CSV in the unified Subgroups header */
  exportAllRef?: MutableRefObject<(() => void) | null>;
}

function cleanSpecialty(desc: string | null | undefined) {
  if (!desc) return null;
  return desc.replace(/^Physician\//i, '').trim() || desc;
}

function suggestedForSpecialty(mvps: MvpDefinition[], specialtyHint: string | null, dataService: MipsDataService) {
  if (!specialtyHint) return [] as MvpDefinition[];
  const xwalk = dataService.getSpecialtyCrosswalk();
  return mvps.filter(m => m.specialtiesMostApplicableTo.some(s => {
    const entry = xwalk.mvpSpecialtyToCodes?.[s];
    const codes = entry?.medicareSpecialtyCodes ?? entry?.codes ?? [];
    return codes.includes(specialtyHint);
  }));
}

export function SubgroupMvpAssignSection({
  practiceId, programYear, selections, onManageMembers,
  embedded = false, exportAllRef,
}: SubgroupMvpAssignSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const dataService = MipsDataService.getInstance();
  const subgroupsQ = useSubgroups(practiceId, programYear);
  const profilesQ = useNpiProfiles(practiceId, programYear);
  const subgroups = subgroupsQ.data ?? [];
  const profiles = profilesQ.data?.profiles ?? [];
  const mvps = useMemo(() => dataService.getMvps(programYear), [dataService, programYear]);

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [pickerSg, setPickerSg] = useState<Subgroup | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSpecialty, setPickerSpecialty] = useState('all');

  const profileByNpi = useMemo(() => {
    const m = new Map<string, NpiProfileRow>();
    for (const p of profiles) m.set(p.npi, p);
    return m;
  }, [profiles]);

  const selectionBySubgroup = useMemo(() => {
    const m = new Map<number, SubgroupSelectionRow>();
    for (const s of selections) {
      if (s.entity_type === 'subgroup' && s.subgroup_id != null) m.set(s.subgroup_id, s);
    }
    return m;
  }, [selections]);

  const specialtyOptions = useMemo(() => {
    const set = new Set<string>();
    for (const m of mvps) {
      for (const s of m.specialtiesMostApplicableTo) set.add(s);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [mvps]);

  const invalidate = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['mvp', 'selections', practiceId, programYear] }),
    queryClient.invalidateQueries({ queryKey: ['mvp', 'subgroups', practiceId, programYear] }),
  ]);

  const toggleExpanded = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const assignMvp = async (sg: Subgroup, mvpId: string) => {
    setBusyKey(`${sg.id}:${mvpId}`);
    try {
      await apiClient.post(`/api/mvp/${practiceId}/selections?year=${programYear}`, {
        mvp_id: mvpId, entity_type: 'subgroup', subgroup_id: sg.id,
      });
      await invalidate();
      const m = mvps.find(x => x.mvpId === mvpId);
      toast({ title: 'Subgroup MVP set', description: `${sg.name} → ${m?.title ?? mvpId}` });
      setPickerSg(null);
    } catch (e: any) {
      toast({ title: 'Failed to assign MVP', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setBusyKey(null);
    }
  };

  const clearMvp = async (sg: Subgroup, selectionId: number) => {
    setBusyKey(`${sg.id}:clear`);
    try {
      await apiClient.delete(`/api/mvp/${practiceId}/selections/${selectionId}?year=${programYear}`);
      await invalidate();
      toast({ title: 'Subgroup MVP cleared', description: sg.name });
    } catch (e: any) {
      toast({ title: 'Failed to clear MVP', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setBusyKey(null);
    }
  };

  const setRegistered = async (sg: Subgroup, selection: SubgroupSelectionRow, registered: boolean) => {
    setBusyKey(`${sg.id}:registered`);
    try {
      await apiClient.patch(`/api/mvp/${practiceId}/selections/${selection.id}?year=${programYear}`, {
        registered,
      });
      await invalidate();
      toast({
        title: registered ? `Registered — ${selection.mvp_id}` : `Registration cleared — ${selection.mvp_id}`,
        description: sg.name,
      });
    } catch (e: any) {
      toast({ title: 'Failed to update registration', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setBusyKey(null);
    }
  };

  const deleteSubgroup = async (sg: Subgroup) => {
    if (!window.confirm(`Delete subgroup “${sg.name}”? Members return to the available roster.`)) return;
    setBusyKey(`${sg.id}:delete`);
    try {
      await apiClient.delete(`/api/mvp/${practiceId}/subgroups/${sg.id}?year=${programYear}`);
      await invalidate();
      toast({ title: 'Subgroup deleted', description: sg.name });
    } catch (e: any) {
      toast({ title: 'Failed to delete subgroup', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setBusyKey(null);
    }
  };

  const openPicker = (sg: Subgroup) => {
    setPickerSearch('');
    setPickerSpecialty('all');
    setPickerSg(sg);
  };

  const pickerSuggested = useMemo(
    () => (pickerSg ? suggestedForSpecialty(mvps, pickerSg.specialty_hint, dataService) : []),
    [pickerSg, mvps, dataService],
  );

  const pickerList = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const suggestedIds = new Set(pickerSuggested.map(m => m.mvpId));
    return mvps
      .filter(m => {
        if (pickerSpecialty !== 'all' && !m.specialtiesMostApplicableTo.includes(pickerSpecialty)) {
          return false;
        }
        if (!q) return true;
        const hay = [m.mvpId, m.title, m.clinicalTopic, ...(m.specialtiesMostApplicableTo ?? [])]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const as = suggestedIds.has(a.mvpId) ? 0 : 1;
        const bs = suggestedIds.has(b.mvpId) ? 0 : 1;
        if (as !== bs) return as - bs;
        return a.title.localeCompare(b.title);
      });
  }, [mvps, pickerSearch, pickerSpecialty, pickerSuggested]);

  const subgroupExportMeta = (sg: Subgroup) => {
    const sel = selectionBySubgroup.get(sg.id);
    const assigned = sel ? mvps.find(m => m.mvpId === sel.mvp_id) : null;
    return {
      mvpId: assigned?.mvpId ?? '',
      mvpTitle: assigned?.title ?? '',
      mvpTopic: assigned?.clinicalTopic ?? '',
      mvpStatus: assigned ? 'assigned' : 'awaiting_mvp_selection',
      registeredCms: sel?.registered ? 'Yes' : assigned ? 'No' : '',
    };
  };

  const sortedMemberRows = (sg: Subgroup) =>
    sg.members
      .map(npi => ({ npi, profile: profileByNpi.get(npi) }))
      .sort((a, b) => {
        const an = (a.profile?.clinician_name || a.npi).toLowerCase();
        const bn = (b.profile?.clinician_name || b.npi).toLowerCase();
        return an.localeCompare(bn);
      });

  const rowsForSubgroup = (sg: Subgroup): unknown[][] => {
    const meta = subgroupExportMeta(sg);
    const members = sortedMemberRows(sg);
    if (members.length === 0) {
      return [buildClinicianExportRow({
        programYear, sg, ...meta, npi: '', profile: undefined,
      })];
    }
    return members.map(({ npi, profile }) => buildClinicianExportRow({
      programYear, sg, ...meta, npi, profile,
    }));
  };

  const exportSubgroupsCsv = () => {
    const sorted = [...subgroups].sort((a, b) => a.name.localeCompare(b.name));
    const rows = sorted.flatMap(rowsForSubgroup);
    downloadCsv(
      `mvp-subgroups-${practiceId}-py${programYear}.csv`,
      [...CLINICIAN_EXPORT_HEADERS],
      rows,
    );
    toast({
      title: 'Subgroups exported',
      description: `${subgroups.length} subgroup${subgroups.length === 1 ? '' : 's'} · ${rows.length} clinician row${rows.length === 1 ? '' : 's'}`,
    });
  };

  const exportSubgroupCliniciansCsv = (sg: Subgroup) => {
    const rows = rowsForSubgroup(sg);
    downloadCsv(
      `mvp-subgroup-${slugForFilename(sg.name)}-${practiceId}-py${programYear}.csv`,
      [...CLINICIAN_EXPORT_HEADERS],
      rows,
    );
    toast({
      title: 'Clinician list exported',
      description: `${sg.name} · ${sg.members.length} clinician${sg.members.length === 1 ? '' : 's'}`,
    });
  };

  // Keep parent header Export wired to the latest closure (ref sync during render).
  if (exportAllRef) exportAllRef.current = exportSubgroupsCsv;

  if (subgroups.length === 0) return null;

  const needsCount = subgroups.filter(sg => !selectionBySubgroup.has(sg.id)).length;

  return (
    <div className="space-y-3">
      {!embedded && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Your subgroups</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Clinicians, pathway, and roster edits in one place
              {needsCount > 0 ? ` · ${needsCount} awaiting MVP selection` : ' · all have an MVP'}
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={exportSubgroupsCsv}>
            <Download className="h-3.5 w-3.5 mr-1" />
            Export CSV
          </Button>
        </div>
      )}
      {embedded && (
        <p className="text-xs text-muted-foreground">
          Pathway and members
          {needsCount > 0 ? ` · ${needsCount} awaiting MVP selection` : ' · all have an MVP'}
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2 items-stretch">
        {subgroups.map(sg => {
          const sel = selectionBySubgroup.get(sg.id);
          const assigned = sel ? mvps.find(m => m.mvpId === sel.mvp_id) : null;
          const isRegistered = !!sel?.registered;
          const suggested = suggestedForSpecialty(mvps, sg.specialty_hint, dataService);
          const primarySuggest = suggested[0] ?? null;
          const busy = busyKey?.startsWith(`${sg.id}:`);
          const isOpen = expanded.has(sg.id);
          const memberRows = sg.members
            .map(npi => profileByNpi.get(npi))
            .filter(Boolean) as NpiProfileRow[];
          const hintDesc = sg.specialty_hint
            ? cleanSpecialty(memberRows[0]?.specialty_desc) || sg.specialty_hint
            : null;
          // Gap Mgmt palette: amber (needs MVP) → stronger slate-blue (selected) → teal (registered)
          const cardTone = isRegistered
            ? 'border-[#6d9f9f]/55 bg-[#6d9f9f]/10'
            : assigned
              ? 'border-[#5a7a9f] bg-[#dce6f0]'
              : 'border-amber-300/70 bg-amber-50/80';

          return (
            <Card key={sg.id} className={`shadow-none flex flex-col h-full min-h-[16.5rem] ${cardTone}`}>
              <CardHeader className="p-4 pb-2 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => toggleExpanded(sg.id)}
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isOpen
                        ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                        : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                      <CardTitle className="text-base font-semibold leading-snug truncate">{sg.name}</CardTitle>
                    </div>
                    <CardDescription className="text-xs pl-6 mt-1 flex flex-wrap gap-x-1.5">
                      <span>
                        {sg.members.length} clinician{sg.members.length === 1 ? '' : 's'}
                        {sg.members.length < 2 ? ' · needs 2+' : ''}
                      </span>
                      {hintDesc && <span>· {hintDesc}</span>}
                      <span className="text-muted-foreground/80">
                        · {isOpen ? 'Hide' : 'Show'} clinicians
                      </span>
                    </CardDescription>
                  </button>
                  <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                    {isRegistered ? (
                      <Badge className="bg-[#6d9f9f] text-white hover:bg-[#6d9f9f] font-normal text-[11px] border-0">
                        Registered
                      </Badge>
                    ) : assigned ? (
                      <Badge className="bg-[#5a7a9f] text-white hover:bg-[#5a7a9f] font-normal text-[11px] border-0">
                        Selected
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50/80 font-normal text-[11px]">
                        Awaiting MVP
                      </Badge>
                    )}
                    {assigned && (
                      <Badge className="bg-slate-800 text-white hover:bg-slate-800 font-mono text-[11px] border-0">
                        {assigned.mvpId}
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      disabled={!!busy}
                      title="Delete subgroup"
                      onClick={() => deleteSubgroup(sg)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="px-4 pb-4 pt-2 flex-1 flex flex-col gap-3">
                {/* Same pathway block height in every state so cards match the assigned look */}
                <div className="min-h-[4.25rem] space-y-2">
                  {assigned && sel ? (
                    <>
                      <p className="text-sm font-medium leading-snug line-clamp-2">{assigned.title}</p>
                      <div className="flex items-center justify-between gap-2">
                        <Label
                          htmlFor={`sg-reg-${sg.id}`}
                          className="flex items-center gap-2 text-sm font-normal cursor-pointer"
                          onClick={e => e.stopPropagation()}
                        >
                          <Checkbox
                            id={`sg-reg-${sg.id}`}
                            checked={isRegistered}
                            disabled={!!busy}
                            onCheckedChange={v => setRegistered(sg, sel, v === true)}
                          />
                          Registered with CMS
                        </Label>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-muted-foreground shrink-0"
                          disabled={!!busy}
                          onClick={() => clearMvp(sg, sel.id)}
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          Clear MVP
                        </Button>
                      </div>
                    </>
                  ) : primarySuggest ? (
                    <>
                      <p className="text-sm font-medium leading-snug line-clamp-2">
                        {primarySuggest.title}
                      </p>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground">
                          Suggested · <span className="font-mono">{primarySuggest.mvpId}</span>
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs shrink-0"
                          disabled={!!busy}
                          onClick={() => assignMvp(sg, primarySuggest.mvpId)}
                        >
                          <Star className="h-3.5 w-3.5 mr-1" />
                          Use suggested
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium leading-snug text-muted-foreground">
                        No pathway selected
                      </p>
                      <p className="text-xs text-muted-foreground leading-snug">
                        No specialty suggestion — choose any MVP below.
                      </p>
                    </>
                  )}
                </div>

                {isOpen && (
                  <div className="rounded-md border bg-white/90 px-3 py-2 max-h-56 overflow-y-auto min-h-[5rem]">
                    {memberRows.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">No roster profiles matched these NPIs.</p>
                    ) : (
                      <ul className="divide-y">
                        {memberRows.map(p => (
                          <li key={p.npi} className="py-1.5 flex items-baseline justify-between gap-2 text-sm">
                            <span className="font-medium truncate">
                              {p.clinician_name || 'Name unavailable'}
                            </span>
                            <span className="font-mono text-muted-foreground tabular-nums shrink-0 text-xs">{p.npi}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Primary actions pinned to bottom; export clinicians from the card */}
                <div className="mt-auto space-y-2 border-t border-black/5 pt-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant={assigned ? 'outline' : 'default'}
                      className="h-9 w-full"
                      disabled={!!busy}
                      onClick={() => openPicker(sg)}
                    >
                      {assigned ? 'Change MVP' : 'Choose MVP'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 w-full"
                      disabled={!!busy}
                      onClick={() => onManageMembers(sg)}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />
                      Members
                    </Button>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 w-full text-xs text-muted-foreground"
                    disabled={sg.members.length === 0}
                    onClick={() => exportSubgroupCliniciansCsv(sg)}
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    Export clinicians ({sg.members.length})
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!pickerSg} onOpenChange={open => { if (!open) setPickerSg(null); }}>
        <DialogContent className="max-w-xl max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-3 space-y-1">
            <DialogTitle>Choose MVP</DialogTitle>
            <DialogDescription>
              {pickerSg
                ? `Assign a pathway to ${pickerSg.name}`
                : 'Assign a pathway'}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-3 grid gap-3 sm:grid-cols-2 border-b">
            <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
              Search
              <Input
                className="bg-white"
                placeholder="MVP ID, title, topic…"
                value={pickerSearch}
                onChange={e => setPickerSearch(e.target.value)}
              />
            </Label>
            <Label className="grid gap-1.5 text-xs font-medium text-muted-foreground">
              Suggested specialty
              <Select value={pickerSpecialty} onValueChange={setPickerSpecialty}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Any specialty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any specialty</SelectItem>
                  {specialtyOptions.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Label>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2 min-h-0 max-h-[50vh]">
            {pickerList.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No MVPs match these filters.</p>
            )}
            {pickerList.map(m => {
              const isSuggested = pickerSuggested.some(s => s.mvpId === m.mvpId);
              const currentId = pickerSg ? selectionBySubgroup.get(pickerSg.id)?.mvp_id : null;
              const isCurrent = currentId === m.mvpId;
              const rowBusy = pickerSg ? busyKey === `${pickerSg.id}:${m.mvpId}` : false;
              return (
                <div
                  key={m.mvpId}
                  className={`rounded-md border px-3 py-2.5 flex items-start justify-between gap-3 ${
                    isCurrent ? 'border-primary bg-primary/5' : 'bg-white'
                  }`}
                >
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-xs text-muted-foreground">{m.mvpId}</span>
                      {isSuggested && (
                        <Badge variant="secondary" className="font-normal text-[10px]">
                          <Star className="h-2.5 w-2.5 mr-0.5" />
                          Suggested
                        </Badge>
                      )}
                      {isCurrent && (
                        <Badge className="font-normal text-[10px]">Current</Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium leading-snug">{m.title}</p>
                    {m.clinicalTopic && (
                      <p className="text-xs text-muted-foreground">{m.clinicalTopic}</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={isCurrent ? 'outline' : 'default'}
                    className="shrink-0"
                    disabled={!pickerSg || rowBusy || isCurrent}
                    onClick={() => pickerSg && assignMvp(pickerSg, m.mvpId)}
                  >
                    {isCurrent ? (
                      <><Check className="h-3.5 w-3.5 mr-1" />Assigned</>
                    ) : (
                      'Assign'
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatType(p: NpiProfileRow) {
  return p.type_description || p.clinician_type || null;
}
